import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponseHeaders } from "axios";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  useExportNotificationStatusReport,
  useGetNotificationStatusReports,
} from "../../../../../api/services/reports/reportsApi";
import { MuiBox } from "../../../../../components";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import { getFileNameFromHeader } from "../../../helper";
import { NotificationStatusReportFormKeys } from "../../../modelsAndSchemas/models";
import { NotificationStatusReportFormSchema } from "../../../modelsAndSchemas/ValidationSchema";
import { NotificationStatusReportResult } from "../notificationStatusReportResult/NotificationStatusReportResult";
import { NotificationStatusReportForm } from "../notificationStatusReportForm/NotificationStatusReportForm";
import { NotificationStatusReportSummary } from "../../../../../api/services/reports/models";

export const NotificationStatusReportSearch = () => {
  const {
    commonValidationMessages: { requiredValidation },
    commonLabels: { someThingWentWrongPleaseTryAgainLater },
  } = useSiteCore();
  const methods = useForm<NotificationStatusReportFormKeys>({
    defaultValues: {
      marketOfDeliveryOffices: [],
    },
    resolver: yupResolver(
      NotificationStatusReportFormSchema(requiredValidation),
    ),
  });
  const [notificationReportList, setNotificationReportList] = useState<
    NotificationStatusReportSummary[]
  >([]);

  const {
    mutateAsync: getNotificationStatusReports,
    data: notificationStatusReportList,
    isPending: isNotificationStatusReportLoading,
    isSuccess: isGetNotificationStatusReportSuccess,
    isError: isGetNotificationStatusReportError,
    isFiltered: isNotificationStatusReportFiltered,
    filterParams: notificationStatusReportListFilteredParams,
  } = useGetNotificationStatusReports();

  const {
    mutateAsync: exportNotificationStatusReport,
    isPending: isExportNotificationStatusReportLoading,
    isError: isExportNotificationStatusReportError,
  } = useExportNotificationStatusReport();

  const dispatch = useDispatch();
  const pendingBillingSearchFormRef = useRef<{ reset: () => void }>(null);

  const isNotificationStatusApiLoading =
    isNotificationStatusReportLoading ||
    isExportNotificationStatusReportLoading;
  const isNotificationStatusApiError =
    isGetNotificationStatusReportError || isExportNotificationStatusReportError;

  useEffect(() => {
    if (isNotificationStatusApiLoading) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isNotificationStatusApiLoading]);

  useEffect(() => {
    if (isGetNotificationStatusReportSuccess) {
      setNotificationReportList(notificationStatusReportList?.data ?? []);
    }
  }, [isGetNotificationStatusReportSuccess, notificationStatusReportList]);

  useEffect(() => {
    if (isNotificationStatusApiError) {
      dispatch(
        toggleToast({
          open: true,
          message: someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isNotificationStatusApiError]);

  const { handleSubmit } = methods;
  const handleSearchFormSubmit = (data: NotificationStatusReportFormKeys) => {
    getNotificationStatusReports({
      marketIds: data.marketOfDeliveryOffices,
    });
  };

  const handleExport = async () => {
    const response = await exportNotificationStatusReport(
      notificationStatusReportListFilteredParams,
    );
    const filename = getFileNameFromHeader(
      response.headers as AxiosResponseHeaders,
    );
    saveAs(response.data, filename);
  };

  return (
    <MuiBox>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSearchFormSubmit)}>
          <NotificationStatusReportForm
            ref={pendingBillingSearchFormRef}
            getNotificationStatusReports={getNotificationStatusReports}
          />
        </form>
      </FormProvider>
      <NotificationStatusReportResult
        onExport={handleExport}
        isFilteredNotificationStatusReport={isNotificationStatusReportFiltered}
        notificationStatusReports={notificationReportList}
      />
    </MuiBox>
  );
};
